import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown, Home, Info, Briefcase, Mail, Laptop, Smartphone, Cpu, Shield, Compass, Activity, Brain, Palette, Share2, BarChart3, Users, Layers, ArrowRight, LogIn, ChevronRight } from 'lucide-react'
import Logo from './Logo.jsx'
import { services } from '../data/services.js'
import { getSessionId, useCurrentUser } from '../auth/currentUser.js'
import { apiAsset } from '../lib/apiAsset.js'

// Floating light-pill navbar (matches the reference design).

/* ------------------------------------------------------------------ *
 *  Mobile sidebar motion
 *
 *  The panel used to slide in flat, all at once. Now it arrives in
 *  layers: the backdrop dims and blurs, the panel eases in on a spring
 *  curve with a lit leading edge, and the rows cascade behind it, each
 *  one a beat after the last. Closing runs shorter than opening, which
 *  is what makes a panel feel responsive rather than sluggish.
 *
 *  Everything is transform and opacity, so it composites on the GPU and
 *  holds 60fps on a mid-range phone. prefers-reduced-motion collapses
 *  the whole thing to a plain fade.
 * ------------------------------------------------------------------ */
const SIDEBAR_MOTION_CSS = `
@keyframes sx-nav-in {
  from { opacity: 0; transform: translate3d(-100%, 0, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
}
@keyframes sx-nav-row {
  from { opacity: 0; transform: translate3d(-18px, 0, 0); }
  to   { opacity: 1; transform: none; }
}
@keyframes sx-nav-edge {
  from { opacity: 0; transform: scaleY(0.2); }
  to   { opacity: 1; transform: scaleY(1); }
}
@keyframes sx-nav-foot {
  from { opacity: 0; transform: translate3d(0, 16px, 0); }
  to   { opacity: 1; transform: none; }
}

/* Opening: a long, soft overshoot curve. Closing: short and linear-ish,
   so dismissing never feels like waiting. */
.sx-nav-panel {
  transition: transform 260ms cubic-bezier(0.4, 0, 1, 1), visibility 260ms;
}
.sx-nav-panel.is-open {
  animation: sx-nav-in 520ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
.sx-nav-panel.is-open .sx-nav-edge {
  transform-origin: top;
  animation: sx-nav-edge 700ms cubic-bezier(0.22, 1, 0.36, 1) 120ms both;
}
.sx-nav-panel.is-open .sx-nav-row {
  animation: sx-nav-row 460ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: calc(160ms + var(--row, 0) * 32ms);
}
.sx-nav-panel.is-open .sx-nav-foot {
  animation: sx-nav-foot 520ms cubic-bezier(0.22, 1, 0.36, 1) 260ms both;
}

@media (prefers-reduced-motion: reduce) {
  .sx-nav-panel.is-open,
  .sx-nav-panel.is-open .sx-nav-edge,
  .sx-nav-panel.is-open .sx-nav-row,
  .sx-nav-panel.is-open .sx-nav-foot {
    animation: none !important;
  }
  .sx-nav-panel { transition: opacity 160ms linear; }
}
`;

/* Ordered by priority, not alphabetically. This one list drives the desktop
   dropdown and the mobile menu, so the two can never drift apart. */
const servicesMenu = [
  { slug: 'website-development', title: 'Website Development' },
  { slug: 'app-development', title: 'App Development' },
  { slug: 'software-development', title: 'Software Development' },
  { slug: 'cyber-security', title: 'Cyber Security' },
  { slug: 'ai-automation', title: 'AI & Automation' },
  { slug: 'iot-development', title: 'IoT Projects' },
  { slug: 'ui-ux-design', title: 'UI / UX Design' },
  { slug: 'digital-marketing', title: 'Digital Marketing' },
  { slug: 'business-consulting', title: 'Business Consulting' },
  { slug: 'social-media-management', title: 'Social Media Management' },
  { slug: 'drone-development', title: 'Drone Development' },
]

const getServiceIcon = (slug) => {
  switch (slug) {
    case 'website-development':
      return Laptop
    case 'app-development':
      return Smartphone
    case 'software-development':
      return Cpu
    case 'cyber-security':
      return Shield
    case 'drone-development':
      return Compass
    case 'iot-development':
      return Activity
    case 'ai-automation':
      return Brain
    case 'ui-ux-design':
      return Palette
    case 'social-media-management':
      return Share2
    case 'digital-marketing':
      return BarChart3
    case 'business-consulting':
      return Users
    default:
      return Laptop
  }
}

/* =========================================================================
   useAdaptiveNavBg — makes the strip behind the floating navbar take on the
   background of whatever section is currently beneath it, so the header
   blends into the page instead of sitting on a fixed white/near-black band.

   Sampling rather than a per-route lookup on purpose: the old hard-coded map
   silently fell back to white, which is why dark heroes (social media, drone)
   showed a white band above them. Sampling also tracks *scroll*, so the strip
   keeps blending as you move between light and dark sections.
   ========================================================================= */
function useAdaptiveNavBg(pathname, stripRef, fallback, onTone) {

  useEffect(() => {
    /* Throttled with a timer rather than requestAnimationFrame on purpose:
       rAF is paused in hidden/background tabs, which left the pending flag
       latched and silently swallowed every later scroll. */
    let tick = 0

    /* Only a full-bleed element counts as "the section". Without this the
       sample would latch onto a card or image that happens to sit under the
       probe point and paint the strip the wrong colour. */
    /* Splits a computed background-image into its comma-separated layers
       without breaking on the commas inside rgba() and inside each gradient's
       own argument list. */
    const splitLayers = (value) => {
      const out = []
      let depth = 0
      let start = 0
      for (let i = 0; i < value.length; i++) {
        const c = value[i]
        if (c === '(') depth++
        else if (c === ')') depth--
        else if (c === ',' && depth === 0) { out.push(value.slice(start, i)); start = i + 1 }
      }
      out.push(value.slice(start))
      return out.map((v) => v.trim()).filter(Boolean)
    }

    /* Describes a layer well enough to reproduce it on another box.

       A layer that sizes itself (a 32px grid, a dot lattice) keeps that size
       and keeps repeating; only its origin moves. A layer left at `auto` is a
       wash stretched over its element, so it has to be given that element's
       box explicitly or the percentages inside it would resolve against the
       strip instead. */
    const tiling = (cs, image, rect) => {
      const selfSized = !(cs.backgroundSize || '').split(',').every((v) => v.trim() === 'auto')
      return selfSized
        ? { image, rect, size: cs.backgroundSize, repeat: cs.backgroundRepeat }
        : { image, rect }
    }

    const sectionLike = (el) => el.getBoundingClientRect().width >= window.innerWidth * 0.9

    /* Returns { image | color, rect } so the caller can re-project a gradient
       onto the strip's own box. */
    const paintOf = (el) => {
      const cs = getComputedStyle(el)

      /* A wash the element itself fades cannot be reproduced by copying only
         its background, so leave those to the layer underneath rather than
         painting them at full strength. */
      if (parseFloat(cs.opacity || '1') < 0.9) return null

      const m = /rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)/.exec(cs.backgroundColor || '')
      const alpha = m ? (m[4] === undefined ? 1 : parseFloat(m[4])) : 0
      if (alpha > 0.55) return { color: cs.backgroundColor }   // a real solid fill wins

      const img = cs.backgroundImage
      if (img && img !== 'none' && !img.startsWith('repeating-')) {
        return tiling(cs, img, el.getBoundingClientRect())
      }
      return null
    }

    /* The decorative layers a section paints itself with.

       They are non-interactive by design, so hit testing never reports them
       and the walk below cannot see them. Collected in reverse document order
       because a later sibling paints over an earlier one, and the first entry
       in a background-image list is the topmost layer.

       Bounded to two levels: a wash is a child or grandchild of the section it
       decorates, and this runs on every scroll sample — walking a whole
       section's subtree four times a second is not worth the one exotic case
       it would catch. */
    const overlaysOf = (el, x, y) => {
      const found = []
      for (const child of el.querySelectorAll(':scope > *, :scope > * > *')) {
        const cs = getComputedStyle(child)
        /* Anything hit-testable is already in the walk; taking it here too
           would paint it twice. */
        if (cs.pointerEvents !== 'none') continue
        if (parseFloat(cs.opacity || '1') < 0.9) continue

        const img = cs.backgroundImage
        if (!img || img === 'none' || img.startsWith('repeating-')) continue

        const r = child.getBoundingClientRect()
        if (r.width < window.innerWidth * 0.9) continue
        if (r.left > x || r.right < x || r.top > y || r.bottom < y) continue
        found.push(tiling(cs, img, r))
      }
      return found.reverse()
    }

    const sample = () => {
      tick = 0

      /* A hidden or zero-size viewport makes elementsFromPoint return nothing,
         which would collapse the strip to the fallback and flip the pill to
         its dark skin — a visible flash when the tab comes back. Hold the last
         good reading instead. */
      if (!window.innerWidth || !window.innerHeight) return
      // Probe just below the strip, near the left edge — section backgrounds
      // run full width while content sits inside a padded container.
      const y = (window.matchMedia('(min-width: 768px)').matches ? 96 : 76) + 6

      // Gradient washes are usually translucent and sit over a solid section
      // colour, so collect them top-down and stack the solid underneath as the
      // final layer — otherwise the strip renders the wash over nothing.
      const node = stripRef.current
      if (!node) return

      /* Resting at the top: the hero is already behind the header, so
         anything painted here would only cover it. */
      if (window.scrollY <= 4) {
        if (node.dataset.bg !== 'top') {
          node.dataset.bg = 'top'
          node.style.backgroundImage = ''
          node.style.background = 'transparent'
        }
        return
      }

      const stripRect = node.getBoundingClientRect()

      const layers = []
      for (const el of document.elementsFromPoint(6, y)) {
        if (el.closest('[data-nav-chrome]')) continue          // skip our own chrome
        if (el.getAttribute('aria-hidden') === 'true') continue
        if (!sectionLike(el)) continue

        /* The section's own washes sit above it, so they go in first. */
        layers.push(...overlaysOf(el, 6, y))

        const paint = paintOf(el)
        if (!paint) continue
        layers.push(paint)
        if (paint.color) break                                 // hit the solid base
      }

      /* Re-project each gradient onto the strip. Sizing every layer to the
         source element's box and offsetting it by the distance between the two
         boxes makes the strip paint the rows of that same gradient that fall
         above the section — a continuation rather than a second, smaller copy
         of the whole thing. */
      const images = []
      const sizes = []
      const positions = []
      const repeats = []
      let baseColor = ''

      for (const layer of layers) {
        if (layer.color) { baseColor = layer.color; continue }
        const r = layer.rect
        const dx = Math.round(r.left - stripRect.left) + 'px'
        const dy = Math.round(r.top - stripRect.top) + 'px'
        /* One element can carry several comma-separated gradients; each needs
           its own size, position and repeat entry or the shorthand lists fall
           out of step and the browser cycles them. */
        const own = layer.size ? splitLayers(layer.size) : null
        const ownRepeat = layer.repeat ? splitLayers(layer.repeat) : null
        const count = splitLayers(layer.image).length
        images.push(layer.image)
        for (let i = 0; i < count; i++) {
          sizes.push(own ? own[i % own.length] : Math.round(r.width) + 'px ' + Math.round(r.height) + 'px')
          positions.push(dx + ' ' + dy)
          repeats.push(ownRepeat ? ownRepeat[i % ownRepeat.length] : 'no-repeat')
        }
      }

      const next = images.length
        ? images.join(', ') + ' | ' + sizes.join(', ') + ' | ' + positions.join(', ') +
          ' | ' + repeats.join(', ') + ' | ' + baseColor
        : (baseColor || fallback)

      if (node.dataset.bg !== next) {
        node.dataset.bg = next
        if (images.length) {
          node.style.background = ''
          node.style.backgroundColor = baseColor || 'transparent'
          node.style.backgroundImage = images.join(', ')
          node.style.backgroundSize = sizes.join(', ')
          node.style.backgroundPosition = positions.join(', ')
          node.style.backgroundRepeat = repeats.join(', ')
        } else {
          node.style.backgroundImage = ''
          node.style.background = baseColor || fallback
        }
      }

      /* Relative luminance of the base layer decides which skin the pill
         wears. The base is the last layer collected — the solid the gradients
         were stacked over — because a translucent wash tells you nothing about
         how light the section reads. */
      if (!layers.length) return   // nothing resolved; keep the current tone
      const base = baseColor
      const m2 = /rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)/.exec(base || '')
      if (m2) {
        const lin = (v) => {
          v = +v / 255
          return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
        }
        const L = 0.2126 * lin(m2[1]) + 0.7152 * lin(m2[2]) + 0.0722 * lin(m2[3])
        /* 0.32 rather than 0.5: the mid-tone navy sections still want the dark
           pill, and flipping too eagerly makes the header strobe on scroll. */
        onTone(L > 0.32 ? 'light' : 'dark')
      }
    }

    /* 28ms is under two frames. At 80ms the strip stayed on the previous
       section for about five frames as a boundary crossed the probe line,
       which is long enough to see as a flash of the wrong colour. */
    const onScroll = () => { if (!tick) tick = setTimeout(sample, 28) }

    /* React no longer paints this node, so put the fallback down immediately
       and let the first sample refine it. */
    if (stripRef.current && !stripRef.current.dataset.bg) {
      stripRef.current.style.background = fallback
    }

    // Let the route's first paint land before the first sample.
    const first = setTimeout(sample, 60)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)

    /* Safety net. Scroll events can be coalesced or throttled (background
       tabs, smooth-scroll, anchor jumps, layout that shifts under a static
       viewport), and a missed one would leave the strip showing the previous
       section. One elementsFromPoint call every 400ms is cheap, and React
       bails out when the sampled value has not changed. */
    const poll = setInterval(sample, 250)

    return () => {
      clearTimeout(first)
      if (tick) clearTimeout(tick)
      clearInterval(poll)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [pathname, stripRef, fallback, onTone])
}

export default function Navbar() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [mobileServices, setMobileServices] = useState(true)
  /* The dropdown used to be pure CSS :hover. After clicking through to a
     service the pointer is still sitting where the menu was, so :hover
     stayed true and the panel hung open over the new page. Holding it in
     state lets the route change close it. */
  const [servicesOpen, setServicesOpen] = useState(false)
  /* Profile details come from the API keyed on the stored session id — the
     only thing localStorage holds. */
  const { user } = useCurrentUser()
  const isLoggedIn = Boolean(getSessionId())
  const userName = user?.name || 'Client'
  const userAvatar = user?.profile_pic || ''

  const location = useLocation()
  const pathname = location.pathname

  /* Fallback only. The strip normally samples whatever section is actually
     sitting under the header (see useAdaptiveNavBg); this is what it falls
     back to before the first sample lands, or if nothing full-bleed is found. */
  const getNavbarBg = () => {
    if (pathname === '/' || pathname === '') {
      return 'radial-gradient(100% 90% at 50% -10%, rgb(231, 240, 255) 0%, rgb(246, 245, 255) 35%, rgb(255, 255, 255) 75%)'
    }
    return '#020617'
  }

  const stripRef = useRef(null)
  /* 'dark' | 'light' — which skin the pill wears over the current section. */
  const [tone, setTone] = useState('dark')
  const onTone = useCallback((t) => setTone((prev) => (prev === t ? prev : t)), [])
  useAdaptiveNavBg(pathname, stripRef, getNavbarBg(), onTone)
  const isLight = tone === 'light'

  useEffect(() => { setServicesOpen(false) }, [pathname])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (open) {
      setMobileServices(true)
    }
  }, [open])

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? 'text-white' : 'text-slate-300 hover:text-white'}`

  const mobileLinkClass = ({ isActive }) =>
    `rounded-xl px-3 py-2 text-sm font-medium transition-colors flex items-center w-full ${
      isActive ? 'bg-white/10 text-white' : 'text-slate-200 hover:bg-white/5'
    }`

  return (
    <>
      <div
        ref={stripRef}
        data-nav-chrome
        className="fixed inset-x-0 top-0 z-40 h-[76px] md:h-[96px] pointer-events-none transition-[background-color] duration-150"
        /* No `style` prop on purpose. React would write the `background`
           SHORTHAND here on every render, and the shorthand resets
           background-position and background-size — silently undoing the
           projection the sampler had just set and putting the seam back. The
           effect owns every background property on this node, including the
           first paint. */
      />
      <header data-nav-chrome className="pointer-events-none fixed inset-x-0 top-4 z-50 px-3 sm:top-5 sm:px-5">
        <div
          /* The pill is fixed navy on every page and every section, on purpose.
             It is the one constant the eye can lock onto while the background
             behind it changes; making it adapt too was a mistake. */
          className={`pointer-events-auto mx-auto flex w-full max-w-[1290px] items-center justify-between gap-3 rounded-full border border-white/10 bg-ink-950/85 px-3 py-2 backdrop-blur-xl transition-all duration-300 sm:px-4 ${
            scrolled ? 'shadow-lg shadow-black/30' : ''
          }`}
        >
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden items-center gap-7 lg:flex">
            <NavLink to="/" end className={linkClass}>Home</NavLink>

            {/* Services dropdown */}
            <div
              className="group relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
              onFocus={() => setServicesOpen(true)}
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setServicesOpen(false) }}
            >
              <NavLink
                to="/services"
                className={({ isActive }) => `inline-flex items-center gap-1 ${linkClass({ isActive })}`}
              >
                Services <ChevronDown className={`h-4 w-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </NavLink>
              <div
                className={
                  'absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-4 transition-all duration-200 ' +
                  (servicesOpen ? 'visible translate-y-0 opacity-100' : 'invisible translate-y-2 opacity-0')
                }
              >
                <div className="rounded-2xl border border-white/10 bg-[#070b1e] p-2 shadow-2xl shadow-black/40">
                  {servicesMenu.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      onClick={() => setServicesOpen(false)}
                      className="block rounded-xl px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white"
                    >
                      {s.title}
                    </Link>
                  ))}
                  <Link
                    to="/services"
                    onClick={() => setServicesOpen(false)}
                    className="block rounded-xl px-3 py-2 text-sm font-semibold text-brand-300 hover:bg-white/5"
                  >
                    View all services →
                  </Link>
                </div>
              </div>
            </div>

          <NavLink to="/portfolio" className={linkClass}>Portfolio</NavLink>
          <NavLink to="/careers" className={linkClass}>Careers</NavLink>
          <NavLink to="/about" className={linkClass}>About</NavLink>
          <NavLink to="/contact" className={linkClass}>Contact Us</NavLink>
        </nav>

        {/* Desktop actions */}
        <div className="hidden items-center gap-2.5 lg:flex">
          {isLoggedIn ? (
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md transition hover:bg-blue-500/20 hover:border-blue-400/50"
            >
              <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-blue-600 text-[11px] font-bold text-white shadow-sm">
                {userAvatar ? (
                  <img
                    src={apiAsset(userAvatar)}
                    alt={userName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  userName ? userName.charAt(0).toUpperCase() : 'U'
                )}
              </div>
              <span>My Dashboard</span>
            </Link>
          ) : (
            <Link
              to="/login"
              className="rounded-full bg-white/95 px-5 py-2.5 text-sm font-semibold text-ink-950 transition hover:bg-white"
            >
              Client login
            </Link>
          )}
          <Link
            to="/contact"
            className="rounded-full bg-[#1d4ed8] px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-blue-500/30 transition hover:bg-[#1e40af] hover:shadow-blue-500/40"
          >
            Talk to us
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center justify-center rounded-full p-2 text-slate-300 hover:bg-white/5 lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      <div
        className={`fixed inset-0 z-40 bg-slate-950/70 transition-all duration-500 lg:hidden ${
          open ? 'pointer-events-auto opacity-100 backdrop-blur-md' : 'pointer-events-none opacity-0 backdrop-blur-0'
        }`}
        onClick={() => setOpen(false)}
      />

      {/* Mobile sidebar panel */}
      <div
        className={`sx-nav-panel pointer-events-auto fixed inset-y-0 left-0 z-50 flex w-[300px] max-w-[85vw] flex-col border-r border-white/10 bg-ink-950/95 p-6 shadow-2xl backdrop-blur-xl lg:hidden ${
          open ? 'is-open translate-x-0' : '-translate-x-full invisible'
        }`}
      >
        <style>{SIDEBAR_MOTION_CSS}</style>

        {/* A lit seam down the opening edge, drawn top-to-bottom as the
            panel lands. It is what makes the entrance read as deliberate. */}
        <span
          aria-hidden="true"
          className="sx-nav-edge pointer-events-none absolute inset-y-0 right-0 w-px"
          style={{ background: 'linear-gradient(180deg, transparent, #3b82f6 18%, #6366f1 55%, transparent)' }}
        />

        <div className="sx-nav-row flex items-center justify-between border-b border-white/10 pb-4 mb-4" style={{ '--row': 0 }}>
          <Logo />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-full p-2 text-slate-300 hover:bg-white/5"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Sidebar navigation

            One ordered list rather than hand-written links: Home and Portfolio
            first, then every service in priority order, then the company
            pages. Adding a service to servicesMenu puts it in the right place
            here automatically. */}
        <nav className="flex-1 overflow-y-auto pr-1 flex flex-col gap-1">
          {[
            { to: '/', label: 'Home', Icon: Home, end: true },
            { to: '/portfolio', label: 'Portfolio', Icon: Layers },
            ...servicesMenu.map((sv) => ({
              to: `/services/${sv.slug}`,
              label: sv.title,
              Icon: getServiceIcon(sv.slug),
            })),
            { to: '/about', label: 'About', Icon: Info },
            { to: '/careers', label: 'Careers', Icon: Briefcase },
            { to: '/contact', label: 'Contact Us', Icon: Mail },
          ].map(({ to, label, Icon, end }, i) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} className={(state) => `sx-nav-row ${mobileLinkClass(state)}`} style={{ '--row': i + 1 }}>
              {({ isActive }) => (
                <>
                  <span className={`flex items-center justify-center w-8 h-8 rounded-lg mr-3 transition-colors ${isActive ? 'bg-[#3d82f6]/20 text-[#60a5fa]' : 'bg-white/5 text-slate-400'}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar actions

            Two full-width pills stacked with a caption underneath ate about a
            third of a phone screen, which is a third of the menu the panel
            exists to show. They are one split control now: a single 48px bar
            where the primary action takes the width it deserves and the
            account sits beside it behind a hairline, the way a toolbar pairs
            a main button with its secondary. Same two destinations, one row. */}
        <div className="sx-nav-foot relative mt-auto shrink-0">
          {/* Lets the list dissolve into the footer instead of ending on a line. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-b from-transparent to-[#0b1120]"
          />

          <div className="border-t border-white/10 bg-[#0b1120] pt-3.5">
            <div
              className="flex h-12 overflow-hidden rounded-xl"
              style={{
                background: 'linear-gradient(90deg, #2563eb 0%, #4f46e5 100%)',
                boxShadow: '0 14px 30px -14px rgba(37,99,235,0.9)',
              }}
            >
              {/* The action this panel is really for, so it takes the width. */}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="group flex flex-1 items-center justify-center gap-2 px-4 text-sm font-bold text-white transition-colors hover:bg-white/10"
              >
                Talk to us
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>

              {/* A hairline rather than a gap: one control, two halves. */}
              <span aria-hidden="true" className="my-2 w-px shrink-0 bg-white/25" />

              {isLoggedIn ? (
                <Link
                  to="/portal"
                  onClick={() => setOpen(false)}
                  title={`${userName} — my dashboard`}
                  aria-label={`${userName} — my dashboard`}
                  className="group flex shrink-0 items-center gap-2 px-3.5 transition-colors hover:bg-white/10"
                >
                  <span className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-white/20 text-[11px] font-bold text-white ring-1 ring-white/40">
                    {userAvatar ? (
                      <img
                        src={apiAsset(userAvatar)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      userName.charAt(0).toUpperCase()
                    )}
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 text-white/70 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="flex shrink-0 items-center gap-1.5 px-4 text-[13px] font-semibold text-white/90 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <LogIn className="h-3.5 w-3.5" />
                  Log in
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
    </>
  )
}
